import { useState } from 'react'
import type { CreateAssetCheckPayload } from '@/types/assetCheck'

interface AssetCheckFormProps {
  clusters: Array<{ name: string; label: string }>
  onSubmit: (payload: CreateAssetCheckPayload) => void
  isSubmitting: boolean
}

const ISIN_PATTERN = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/

export function AssetCheckForm({ clusters, onSubmit, isSubmitting }: AssetCheckFormProps) {
  const [isin, setIsin] = useState('')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState<'EUR' | 'USD' | 'CHF' | 'GBP'>('EUR')
  const [broker, setBroker] = useState<'IBKR' | 'Consors' | 'Scalable' | 'Manual' | ''>('')
  const [clusterOverride, setClusterOverride] = useState('')

  const isinValid = ISIN_PATTERN.test(isin)
  const amountValid = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0
  const canSubmit = isinValid && amountValid && !isSubmitting

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      isin,
      planned_amount: amount,
      planned_currency: currency,
      broker: broker === '' ? null : broker,
      cluster_override: clusterOverride === '' ? null : clusterOverride,
    })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
      data-testid="asset-check-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">ISIN</span>
          <input
            type="text"
            value={isin}
            onChange={(e) => setIsin(e.target.value.toUpperCase())}
            maxLength={12}
            placeholder="IE00B4L5Y983"
            className="mt-1 w-full rounded border-slate-300 px-2 py-1.5"
            data-testid="isin-input"
            required
          />
          {!isinValid && isin.length > 0 && (
            <span className="text-xs text-red-600">ISIN: 12 Zeichen, Format ABxxxxxxxxxN</span>
          )}
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Betrag</span>
          <div className="mt-1 flex">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1000.00"
              className="w-full rounded-l border-slate-300 px-2 py-1.5"
              data-testid="amount-input"
              required
            />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as typeof currency)}
              className="rounded-r border-slate-300 border-l-0 px-2 py-1.5"
              data-testid="currency-select"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="CHF">CHF</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Broker (optional)</span>
          <select
            value={broker}
            onChange={(e) => setBroker(e.target.value as typeof broker)}
            className="mt-1 w-full rounded border-slate-300 px-2 py-1.5"
            data-testid="broker-select"
          >
            <option value="">—</option>
            <option value="IBKR">IBKR</option>
            <option value="Consors">Consors</option>
            <option value="Scalable">Scalable</option>
            <option value="Manual">Manual</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Cluster-Override (optional)</span>
          <select
            value={clusterOverride}
            onChange={(e) => setClusterOverride(e.target.value)}
            className="mt-1 w-full rounded border-slate-300 px-2 py-1.5"
            data-testid="cluster-select"
          >
            <option value="">— auto —</option>
            {clusters.map((c) => (
              <option key={c.name} value={c.name}>
                {c.label}
              </option>
            ))}
            <option value="_none">— keinem zuordnen —</option>
          </select>
        </label>
      </div>
      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
        data-testid="submit-button"
      >
        {isSubmitting ? 'Lade Marktdaten…' : 'Check ausführen'}
      </button>
    </form>
  )
}
